import './Info.css';
import { forwardRef } from 'react';
import { motion } from 'framer-motion';
import OurServices from '@/pages/LandingPage/Info/components/OurServices.tsx';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const mockContracts = [
  { initials: 'AC', name: 'Acme Corporation', email: 'acme@example.com', type: 'Prestação de Serviços', value: 'R$ 85.000', due: '15/03/2024', status: 'Ativo', statusColor: 'bg-green-100 text-green-700' },
  { initials: 'TI', name: 'Tech Innovations Ltd', email: 'tech@example.com', type: 'Fornecimento', value: 'R$ 120.000', due: '22/02/2024', status: 'Renovação', statusColor: 'bg-yellow-100 text-yellow-700' },
  { initials: 'GS', name: 'Global Solutions Inc', email: 'global@example.com', type: 'Consultoria', value: 'R$ 45.000', due: '10/04/2024', status: 'Pendente', statusColor: 'bg-orange-100 text-orange-700' },
];

const benefits = [
  'Automação de vendas e cobranças',
  'Dashboard com indicadores em tempo real',
  'Notificações automáticas para clientes',
  'Relatórios e visão financeira completa',
];

const heroKpis = [
  { value: 'Foco em', label: 'Mais velocidade' },
  { value: 'Foco em', label: 'Mais controle' },
  { value: 'Foco em', label: 'Mais faturamento' },
];

const monthlyBars = [
  { month: 'Jul', pct: 58 },
  { month: 'Ago', pct: 67 },
  { month: 'Set', pct: 74 },
  { month: 'Out', pct: 83 },
  { month: 'Nov', pct: 91 },
  { month: 'Dez', pct: 100 },
];

const RING_R = 68;
const RING_C = 2 * Math.PI * RING_R;
const OCCUPANCY = 87;

const Info = forwardRef<HTMLDivElement, { scrollToSection: (_id: string) => void }>(({ scrollToSection: _scrollToSection }, ref) => {
  return (
    <>
      {/* ── Secao: O que e o Cowonder? ── */}
      <section
        ref={ref}
        className="min-h-screen flex items-center bg-white px-6 py-20"
      >
        <div className="w-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: copy */}
          <motion.div
            className="flex flex-col"
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            viewport={{ once: true }}
          >
            <span className="inline-block text-[--terciary-color] text-sm font-semibold uppercase tracking-widest mb-4">
              Plataforma SaaS
            </span>
            <h2 className="text-[--primary-color] font-bold text-4xl md:text-5xl leading-tight mb-6">
              O que é o Cowonder?
            </h2>
            <p className="text-gray-500 text-lg font-light leading-relaxed mb-8">
              Uma plataforma completa de gestão de contratos que automatiza processos,
              reduz riscos e aumenta a eficiência do seu time comercial.
            </p>

            <ul className="space-y-3">
              {benefits.map((b) => (
                <li key={b} className="flex items-center gap-3 text-[--primary-color] text-sm font-medium">
                  <CheckCircleIcon sx={{ fontSize: 20, color: '#CB88F9' }} />
                  {b}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Right: mock UI card */}
          <motion.div
            className="w-full bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: 'easeOut' }}
            viewport={{ once: true }}
          >
            {/* Card header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[--secondary-color] opacity-80" />
                <span className="text-[--primary-color] font-semibold text-xs">Lista de Contratos</span>
              </div>
              <div className="flex gap-2">
                <button className="text-xs border border-gray-200 rounded-md px-2.5 py-1 text-gray-400">
                  Filtrar
                </button>
                <button className="text-xs bg-[--primary-color] text-white rounded-md px-2.5 py-1">
                  + Novo Contrato
                </button>
              </div>
            </div>

            {/* Table header */}
            <div className="grid grid-cols-5 px-5 py-2.5 bg-gray-50 border-b border-gray-100 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              <span className="col-span-2">Cliente/Fornecedor</span>
              <span>Tipo</span>
              <span>Valor</span>
              <span className="text-[--terciary-color]">Vencimento</span>
            </div>

            {mockContracts.map((c, i) => (
              <motion.div
                key={c.name}
                className="grid grid-cols-5 items-center px-5 py-3.5 border-b border-gray-50 last:border-0 hover:bg-purple-50/40 transition-colors"
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.2 + i * 0.1, ease: 'easeOut' }}
                viewport={{ once: true }}
              >
                <div className="col-span-2 flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-[--secondary-color] flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                    {c.initials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[--terciary-color] font-semibold text-xs truncate">{c.name}</p>
                    <p className="text-gray-400 text-[10px] truncate">{c.email}</p>
                  </div>
                </div>
                <span className="text-gray-500 text-xs">{c.type}</span>
                <span className="text-[--primary-color] font-semibold text-xs">{c.value}</span>
                <div className="flex flex-col gap-1">
                  <span className="text-[--terciary-color] text-[10px] font-medium">{c.due}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full w-fit ${c.statusColor}`}>
                    {c.status}
                  </span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Secao: Nossos Servicos ── */}
      <OurServices />

      {/* ── Secao: Inteligencia de Dados ── */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-28 overflow-hidden" style={{ background: '#08001a' }}>

        {/* Aurora blobs */}
        <div className="absolute -top-56 -left-40 w-[700px] h-[700px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(121,28,184,0.4) 0%, transparent 70%)', filter: 'blur(90px)' }} />
        <div className="absolute -bottom-56 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(73,15,112,0.45) 0%, transparent 70%)', filter: 'blur(80px)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[300px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(203,136,249,0.05) 0%, transparent 70%)', filter: 'blur(40px)' }} />

        <div className="relative z-10 w-full max-w-6xl mx-auto">

          {/* Header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            viewport={{ once: true }}
          >
            <span className="inline-block text-[#cb88f9] text-xs font-bold uppercase tracking-widest bg-[#cb88f9]/10 border border-[#cb88f9]/20 px-5 py-2 rounded-full mb-6">
              Inteligência de Dados
            </span>
            <h2
              className="font-extrabold text-5xl md:text-6xl leading-tight mb-5"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #cb88f9 55%, #791cb8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Sua empresa,<br />em números reais.
            </h2>
            <p className="text-white/40 text-base max-w-lg mx-auto leading-relaxed">
              Visualize tendências, preveja receita e tome decisões com dados em tempo real.
            </p>
          </motion.div>

          {/* Hero KPIs */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-0 mb-12 rounded-2xl overflow-hidden"
            style={{ background: 'rgba(203,136,249,0.04)', border: '1px solid rgba(203,136,249,0.1)' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: 'easeOut', delay: 0.1 }}
            viewport={{ once: true }}
          >
            {heroKpis.map((k, i) => (
              <>
                <div key={k.label} className="flex-1 flex flex-col items-center justify-center py-8 px-6 text-center">
                  <span className="inline-block text-[#cb88f9] text-[10px] font-bold uppercase tracking-widest bg-[#cb88f9]/10 border border-[#cb88f9]/20 px-3 py-1 rounded-full mb-3">
                    {k.value}
                  </span>
                  <p
                    className="font-extrabold text-2xl md:text-3xl leading-tight"
                    style={{
                      background: i === 1
                        ? 'linear-gradient(135deg, #cb88f9 0%, #fff 100%)'
                        : 'linear-gradient(135deg, #fff 0%, #cb88f9 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {k.label}
                  </p>
                </div>
                {i < heroKpis.length - 1 && (
                  <div className="hidden sm:block w-px self-stretch" style={{ background: 'rgba(203,136,249,0.15)' }} />
                )}
              </>
            ))}
          </motion.div>

          {/* Dashboard panel */}
          <motion.div
            className="rounded-3xl overflow-hidden"
            style={{
              background: 'rgba(15, 5, 30, 0.8)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(203,136,249,0.15)',
              boxShadow: '0 32px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
            }}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            viewport={{ once: true }}
          >
            {/* Window chrome */}
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/70" />
                <div className="w-3 h-3 rounded-full bg-yellow-400/70" />
                <div className="w-3 h-3 rounded-full bg-green-400/70" />
                <span className="ml-3 text-white/30 text-xs">Painel de Crescimento — Cowonder Analytics</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-green-400/80 text-xs font-medium">Ao Vivo</span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] divide-y lg:divide-y-0 lg:divide-x" style={{ '--tw-divide-opacity': 1, borderColor: 'rgba(255,255,255,0.07)' } as React.CSSProperties}>

              {/* Left: Bar chart */}
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-white font-semibold text-sm">Receita Mensal</p>
                    <p className="text-white/35 text-xs mt-0.5">Jul – Dez 2024</p>
                  </div>
                  <span className="text-[#cb88f9] text-xs font-bold bg-[#cb88f9]/10 border border-[#cb88f9]/20 px-3 py-1 rounded-full">+72% no semestre</span>
                </div>

                <div className="flex flex-col gap-4">
                  {monthlyBars.map((bar, i) => (
                    <div key={bar.month} className="flex items-center gap-4">
                      <span className="text-white/40 text-xs w-8 flex-shrink-0">{bar.month}</span>
                      <div className="flex-1 h-6 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <motion.div
                          className="h-full rounded-full"
                          style={{
                            background: bar.pct === 100
                              ? 'linear-gradient(90deg, #cb88f9, #fff)'
                              : 'linear-gradient(90deg, #490f70, #cb88f9)',
                          }}
                          initial={{ width: '0%' }}
                          whileInView={{ width: `${bar.pct}%` }}
                          transition={{ duration: 1, delay: 0.3 + i * 0.1, ease: 'easeOut' }}
                          viewport={{ once: true }}
                        />
                      </div>
                      <span className="text-white/50 text-xs w-8 text-right flex-shrink-0">{bar.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Ring gauge + pills */}
              <div className="p-8 flex flex-col items-center justify-between gap-8">
                {/* Ring */}
                <div>
                  <p className="text-white font-semibold text-sm text-center mb-6">Ocupação Média</p>
                  <div className="relative flex items-center justify-center">
                    <svg width="180" height="180" viewBox="0 0 180 180">
                      <defs>
                        <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#490f70" />
                          <stop offset="100%" stopColor="#cb88f9" />
                        </linearGradient>
                      </defs>
                      {/* Track */}
                      <circle cx="90" cy="90" r={RING_R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="14" />
                      {/* Progress */}
                      <motion.circle
                        cx="90"
                        cy="90"
                        r={RING_R}
                        fill="none"
                        stroke="url(#ringGrad)"
                        strokeWidth="14"
                        strokeLinecap="round"
                        strokeDasharray={RING_C}
                        transform="rotate(-90 90 90)"
                        initial={{ strokeDashoffset: RING_C }}
                        whileInView={{ strokeDashoffset: RING_C * (1 - OCCUPANCY / 100) }}
                        transition={{ duration: 1.6, delay: 0.4, ease: 'easeOut' }}
                        viewport={{ once: true }}
                      />
                    </svg>
                    {/* Center label */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <p className="font-extrabold text-4xl" style={{ background: 'linear-gradient(135deg, #fff, #cb88f9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                        {OCCUPANCY}%
                      </p>
                      <p className="text-white/35 text-[10px] uppercase tracking-wider mt-1">ocupados</p>
                    </div>
                  </div>
                </div>

                {/* Mini metric pills */}
                <div className="grid grid-cols-2 gap-3 w-full">
                  {[
                    { label: 'Crescimento MoM', value: '+23%', color: '#4ade80' },
                    { label: 'Receita Anual', value: 'R$42k', color: '#cb88f9' },
                    { label: 'Contratos Ativos', value: '318', color: '#60a5fa' },
                    { label: 'Churn Rate', value: '1,2%', color: '#f472b6' },
                  ].map((m) => (
                    <div key={m.label} className="rounded-xl p-4 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      <p className="font-bold text-lg" style={{ color: m.color }}>{m.value}</p>
                      <p className="text-white/30 text-[10px] mt-0.5 leading-tight">{m.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
});

export default Info;
